/** Runtime validation of a Session read from IndexedDB or an import file. Errors name the path. */
import type { ToggleState } from "../character/model";
import { ValidationError } from "../character/validate";
import type { ConsumableStack, EffectDuration, GrantLink, Session, SessionCondition, SessionEffect, SpellcastingUse } from "./model";
import { DURATION_UNITS, SESSION_SCHEMA } from "./model";

type Obj = Record<string, unknown>;

function isObj(v: unknown): v is Obj {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function describe(v: unknown): string {
  if (v === null) return "null";
  if (Array.isArray(v)) return "an array";
  return typeof v === "undefined" ? "nothing" : typeof v === "object" ? "an object" : `${typeof v} ${JSON.stringify(v)}`;
}

function str(o: Obj, key: string, path: string): string {
  const v = o[key];
  if (typeof v !== "string" || v.length === 0) throw new ValidationError(`${path}.${key}`, `expected a non-empty string, got ${describe(v)}`);
  return v;
}

function int(o: Obj, key: string, path: string, min = Number.NEGATIVE_INFINITY): number {
  const v = o[key];
  if (typeof v !== "number" || !Number.isInteger(v) || v < min) throw new ValidationError(`${path}.${key}`, `expected an integer${min > Number.NEGATIVE_INFINITY ? ` of at least ${min}` : ""}, got ${describe(v)}`);
  return v;
}

function intOrNull(o: Obj, key: string, path: string, min = 0): number | null {
  const v = o[key];
  if (v === null || v === undefined) return null;
  return int(o, key, path, min);
}

function bool(o: Obj, key: string, path: string): boolean {
  const v = o[key];
  if (typeof v !== "boolean") throw new ValidationError(`${path}.${key}`, `expected a boolean, got ${describe(v)}`);
  return v;
}

function arr(o: Obj, key: string, path: string): unknown[] {
  const v = o[key];
  if (!Array.isArray(v)) throw new ValidationError(`${path}.${key}`, `expected an array, got ${describe(v)}`);
  return v;
}

function obj(o: Obj, key: string, path: string): Obj {
  const v = o[key];
  if (!isObj(v)) throw new ValidationError(`${path}.${key}`, `expected an object, got ${describe(v)}`);
  return v;
}

const DELETE_ACTIONS = new Set(["cascade", "detach", "restrict"]);

function grant(v: unknown, path: string): GrantLink | null {
  if (v === null || v === undefined) return null;
  if (!isObj(v)) throw new ValidationError(path, `expected an object or null, got ${describe(v)}`);
  const onDelete = obj(v, "onDelete", path);
  const granter = str(onDelete, "granter", `${path}.onDelete`);
  const grantee = str(onDelete, "grantee", `${path}.onDelete`);
  if (!DELETE_ACTIONS.has(granter) || !DELETE_ACTIONS.has(grantee)) throw new ValidationError(`${path}.onDelete`, "expected cascade, detach or restrict");
  return { grantedBy: str(v, "grantedBy", path), onDelete: { granter: granter as GrantLink["onDelete"]["granter"], grantee: grantee as GrantLink["onDelete"]["grantee"] } };
}

function condition(v: unknown, path: string): SessionCondition {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  let persistent: SessionCondition["persistent"] = null;
  if (v.persistent !== null && v.persistent !== undefined) {
    const p = obj(v, "persistent", path);
    persistent = { formula: str(p, "formula", `${path}.persistent`), damageType: str(p, "damageType", `${path}.persistent`), dc: int(p, "dc", `${path}.persistent`, 0) };
  }
  return { id: str(v, "id", path), uuid: str(v, "uuid", path), slug: str(v, "slug", path), name: str(v, "name", path), value: intOrNull(v, "value", path, 1), grant: grant(v.grant, `${path}.grant`), persistent };
}

function duration(v: unknown, path: string): EffectDuration {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const unit = str(v, "unit", path);
  if (!(DURATION_UNITS as readonly string[]).includes(unit)) throw new ValidationError(`${path}.unit`, `unknown duration unit ${JSON.stringify(unit)}`);
  const expiry = v.expiry === null || v.expiry === undefined ? null : v.expiry;
  if (expiry !== null && expiry !== "turn-start" && expiry !== "turn-end") throw new ValidationError(`${path}.expiry`, "expected turn-start, turn-end or null");
  return { unit: unit as EffectDuration["unit"], value: int(v, "value", path, 0), expiry, sustained: v.sustained === true };
}

function effect(v: unknown, path: string): SessionEffect {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  return {
    id: str(v, "id", path),
    uuid: str(v, "uuid", path),
    slug: str(v, "slug", path),
    name: str(v, "name", path),
    level: intOrNull(v, "level", path, 0),
    badge: intOrNull(v, "badge", path, 0),
    start: int(v, "start", path, 0),
    duration: duration(v.duration, `${path}.duration`),
    grant: grant(v.grant, `${path}.grant`),
  };
}

function stack(v: unknown, path: string): ConsumableStack {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  let uses: ConsumableStack["uses"] = null;
  if (v.uses !== null && v.uses !== undefined) {
    const u = obj(v, "uses", path);
    const renew = u.renew === "day" ? "day" : null;
    uses = { value: int(u, "value", `${path}.uses`, 0), max: int(u, "max", `${path}.uses`, 0), renew };
  }
  return { id: str(v, "id", path), uuid: str(v, "uuid", path), name: str(v, "name", path), quantity: int(v, "quantity", path, 0), uses, autoDestroy: bool(v, "autoDestroy", path), ammunition: bool(v, "ammunition", path) };
}

function counts(v: unknown, path: string, min = 0): Record<string, number> {
  if (v === undefined) return {};
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const out: Record<string, number> = {};
  for (const key of Object.keys(v)) out[key] = int(v, key, path, min);
  return out;
}

function spellcastingUse(v: unknown, path: string): SpellcastingUse {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const prepared: Record<string, boolean[]> = {};
  if (v.prepared !== undefined) {
    const p = obj(v, "prepared", path);
    for (const rank of Object.keys(p)) {
      const list = arr(p, rank, `${path}.prepared`);
      if (!list.every((b) => typeof b === "boolean")) throw new ValidationError(`${path}.prepared.${rank}`, "expected booleans");
      prepared[rank] = list as boolean[];
    }
  }
  const use: SpellcastingUse = { slots: counts(v.slots, `${path}.slots`), prepared, innate: counts(v.innate, `${path}.innate`) };
  if (v.charges !== undefined) {
    if (typeof v.charges !== "number" || !Number.isInteger(v.charges) || v.charges < 0) throw new ValidationError(`${path}.charges`, `expected a non-negative integer, got ${describe(v.charges)}`);
    use.charges = v.charges;
  }
  return use;
}

function toggles(v: unknown, path: string): Record<string, ToggleState> {
  if (v === undefined) return {};
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const out: Record<string, ToggleState> = {};
  for (const [key, value] of Object.entries(v)) {
    if (!isObj(value)) throw new ValidationError(`${path}.${key}`, `expected an object, got ${describe(value)}`);
    const state: ToggleState = {};
    if (value.checked !== undefined) state.checked = bool(value, "checked", `${path}.${key}`);
    if (value.selection !== undefined) state.selection = str(value, "selection", `${path}.${key}`);
    out[key] = state;
  }
  return out;
}

const ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;

/** Validate an unknown value as a Session. Returns a fresh object holding only known fields. */
export function validateSession(v: unknown, path = "session"): Session {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const schema = int(v, "schema", path);
  if (schema !== SESSION_SCHEMA) throw new ValidationError(`${path}.schema`, `Session schema ${schema} is not ${SESSION_SCHEMA}`);
  const startedAt = str(v, "startedAt", path);
  if (!ISO.test(startedAt)) throw new ValidationError(`${path}.startedAt`, "expected an ISO timestamp");
  const hp = obj(v, "hp", path);
  const conditions = arr(v, "conditions", path).map((c, i) => condition(c, `${path}.conditions[${i}]`));
  const effects = arr(v, "effects", path).map((e, i) => effect(e, `${path}.effects[${i}]`));
  const consumables = arr(v, "consumables", path).map((s, i) => stack(s, `${path}.consumables[${i}]`));
  const ids = [...conditions.map((c) => c.id), ...effects.map((e) => e.id), ...consumables.map((s) => s.id)];
  if (new Set(ids).size !== ids.length) throw new ValidationError(path, "Session ids must be unique");
  const spellcasting: Record<string, SpellcastingUse> = {};
  if (v.spellcasting !== undefined) {
    const sc = obj(v, "spellcasting", path);
    for (const key of Object.keys(sc)) spellcasting[key] = spellcastingUse(sc[key], `${path}.spellcasting.${key}`);
  }
  return {
    schema: SESSION_SCHEMA,
    startedAt,
    clock: int(v, "clock", path, 0),
    round: intOrNull(v, "round", path, 1),
    hp: { value: int(hp, "value", `${path}.hp`, 0), temp: int(hp, "temp", `${path}.hp`, 0) },
    conditions,
    effects,
    heroPoints: int(v, "heroPoints", path, 0),
    focus: int(v, "focus", path, 0),
    spellcasting,
    resources: counts(v.resources, `${path}.resources`),
    uses: counts(v.uses, `${path}.uses`),
    consumables,
    toggles: toggles(v.toggles, `${path}.toggles`),
    counter: int(v, "counter", path, 1),
  };
}
